import type { FastifyRequest, FastifyReply } from 'fastify'
import type { Firestore } from 'firebase-admin/firestore'
import type { RedisClient } from '../../core/redis/client.js'
import type { EventEmitter } from 'node:events'
import type { QuestType } from '@my-roadtrip/types'
import { GamificationService } from './gamification.service'
import { QuestService } from './quest.service'
import { ChallengeService } from './challenge.service'
import { BingoService } from './bingo.service'
import { ApiResponse } from '../../utils/response'

export class GamificationController {
  private readonly service: GamificationService
  private readonly questService: QuestService
  private readonly challengeService: ChallengeService
  private readonly bingoService: BingoService

  constructor(
    firestore: Firestore,
    redis: RedisClient,
    events: EventEmitter
  ) {
    this.service = new GamificationService(firestore, redis)
    this.questService = new QuestService(firestore, redis, events)
    this.challengeService = new ChallengeService(firestore, redis, events)
    this.bingoService = new BingoService(firestore, redis)
  }

  async getProfile(request: FastifyRequest, reply: FastifyReply) {
    try {
      const userId = request.user?.uid
      if (!userId) {
        return ApiResponse.unauthorized(reply)
      }

      const profile = await this.service.getUserProfile(userId)
      return reply.send(profile)
    } catch (error) {
      request.log.error(error)
      return ApiResponse.internalError(reply, 'Failed to get profile')
    }
  }

  async getLeaderboard(
    request: FastifyRequest<{ Querystring: { period?: string; lastDocId?: string; limit?: string } }>,
    reply: FastifyReply
  ) {
    try {
      const raw = request.query || {}
      const period = (raw.period as 'week' | 'month' | 'all') || 'week'
      const lastDocId = raw.lastDocId
      const limitRaw = Number.parseInt(raw.limit || '50', 10) || 50
      const limit = Math.min(Math.max(1, limitRaw), 100)

      const leaderboard = await this.service.getLeaderboard(period, lastDocId, limit)
      return reply.send(leaderboard)
    } catch (error) {
      request.log.error(error)
      return ApiResponse.internalError(reply, 'Failed to get leaderboard')
    }
  }

  async getAchievements(request: FastifyRequest, reply: FastifyReply) {
    try {
      const userId = request.user?.uid
      if (!userId) {
        return ApiResponse.unauthorized(reply)
      }

      const achievements = await this.service.getRecentAchievements(userId)
      return reply.send(achievements)
    } catch (error) {
      request.log.error(error)
      return ApiResponse.internalError(reply, 'Failed to get achievements')
    }
  }

  async claimBadge(
    request: FastifyRequest<{ Params: { badgeId: string } }>,
    reply: FastifyReply
  ) {
    try {
      const userId = request.user?.uid
      if (!userId) {
        return ApiResponse.unauthorized(reply)
      }

      const { badgeId } = request.params

      await this.service.claimBadge(userId, badgeId)
      return reply.send({ success: true })
    } catch (error) {
      request.log.error(error)
      return ApiResponse.internalError(reply, 'Failed to claim badge')
    }
  }

  // ========== FUEL ENDPOINTS ==========

  async getFuel(request: FastifyRequest, reply: FastifyReply) {
    try {
      const userId = request.user?.uid
      if (!userId) {
        return ApiResponse.unauthorized(reply)
      }

      const fuel = await this.service.getFuel(userId)
      return reply.send(fuel)
    } catch (error) {
      request.log.error(error)
      return ApiResponse.internalError(reply, 'Failed to get fuel')
    }
  }

  async spendFuel(
    request: FastifyRequest<{ Body: { amount: number; reason: string } }>,
    reply: FastifyReply
  ) {
    try {
      const userId = request.user?.uid
      if (!userId) {
        return ApiResponse.unauthorized(reply)
      }

      const { amount, reason } = request.body

      if (!amount || amount <= 0) {
        return ApiResponse.badRequest(reply, 'Invalid fuel amount')
      }

      if (!reason) {
        return ApiResponse.badRequest(reply, 'Reason is required')
      }

      const newFuel = await this.service.spendFuel(userId, amount, reason)
      return reply.send({ fuel: newFuel, success: true })
    } catch (error: unknown) {
      request.log.error(error)
      
      if (error instanceof Error && error.message.includes('Insufficient fuel')) {
        return ApiResponse.badRequest(reply, error.message)
      }
      
      return ApiResponse.internalError(reply, 'Failed to spend fuel')
    }
  }

  async regenerateFuel(request: FastifyRequest, reply: FastifyReply) {
    try {
      const userId = request.user?.uid
      if (!userId) {
        return ApiResponse.unauthorized(reply)
      }

      await this.service.regenerateFuel(userId)
      const fuel = await this.service.getFuel(userId)
      return reply.send(fuel)
    } catch (error) {
      request.log.error(error)
      return ApiResponse.internalError(reply, 'Failed to regenerate fuel')
    }
  }

  // ========== QUEST ENDPOINTS ==========

  async getQuests(request: FastifyRequest, reply: FastifyReply) {
    try {
      const userId = request.user?.uid
      if (!userId) {
        return ApiResponse.unauthorized(reply)
      }

      const quests = await this.questService.getUserQuests(userId)
      return reply.send(quests)
    } catch (error) {
      request.log.error(error)
      return ApiResponse.internalError(reply, 'Failed to get quests')
    }
  }

  async getActiveQuests(request: FastifyRequest, reply: FastifyReply) {
    try {
      const userId = request.user?.uid
      if (!userId) {
        return ApiResponse.unauthorized(reply)
      }

      const quests = await this.questService.getActiveQuests(userId)
      return reply.send(quests)
    } catch (error) {
      request.log.error(error)
      return ApiResponse.internalError(reply, 'Failed to get active quests')
    }
  }

  async generateDailyQuests(request: FastifyRequest, reply: FastifyReply) {
    try {
      const userId = request.user?.uid
      if (!userId) {
        return ApiResponse.unauthorized(reply)
      }

      const quests = await this.questService.generateDailyQuests(userId)
      return reply.send(quests)
    } catch (error) {
      request.log.error(error)
      return ApiResponse.internalError(reply, 'Failed to generate daily quests')
    }
  }

  async generateWeeklyQuest(request: FastifyRequest, reply: FastifyReply) {
    try {
      const userId = request.user?.uid
      if (!userId) {
        return ApiResponse.unauthorized(reply)
      }

      const quest = await this.questService.generateWeeklyQuest(userId)
      return reply.send(quest)
    } catch (error) {
      request.log.error(error)
      return ApiResponse.internalError(reply, 'Failed to generate weekly quest')
    }
  }

  async completeQuest(
    request: FastifyRequest<{ Params: { questId: string } }>,
    reply: FastifyReply
  ) {
    try {
      const userId = request.user?.uid
      if (!userId) {
        return ApiResponse.unauthorized(reply)
      }

      const { questId } = request.params
      const success = await this.questService.completeQuest(questId)
      
      if (!success) {
        return ApiResponse.badRequest(reply, 'Quest not found or already completed')
      }

      return reply.send({ success: true })
    } catch (error) {
      request.log.error(error)
      return ApiResponse.internalError(reply, 'Failed to complete quest')
    }
  }

  async trackObjectiveProgress(
    request: FastifyRequest<{ Body: { type: QuestType; amount?: number } }>,
    reply: FastifyReply
  ) {
    try {
      const userId = request.user?.uid
      if (!userId) {
        return ApiResponse.unauthorized(reply)
      }

      const { type, amount = 1 } = request.body ?? {}

      if (!type) {
        return ApiResponse.badRequest(reply, 'Objective type is required')
      }

      const result = await this.questService.trackObjectiveProgress(userId, type, amount)
      return reply.send(result)
    } catch (error) {
      request.log.error(error)
      return ApiResponse.internalError(reply, 'Failed to track objective progress')
    }
  }

  // ========== CHALLENGE ENDPOINTS ==========

  async getChallenges(request: FastifyRequest, reply: FastifyReply) {
    try {
      const userId = request.user?.uid
      if (!userId) {
        return ApiResponse.unauthorized(reply)
      }

      const challenges = await this.challengeService.getUserChallenges(userId)
      return reply.send(challenges)
    } catch (error) {
      request.log.error(error)
      return ApiResponse.internalError(reply, 'Failed to get challenges')
    }
  }

  async getSeasonChallenges(request: FastifyRequest, reply: FastifyReply) {
    try {
      const userId = request.user?.uid
      if (!userId) {
        return ApiResponse.unauthorized(reply)
      }

      const seasonId = this.challengeService.getCurrentSeasonId()
      const challenges = await this.challengeService.getSeasonChallenges(userId, seasonId)
      return reply.send(challenges)
    } catch (error) {
      request.log.error(error)
      return ApiResponse.internalError(reply, 'Failed to get season challenges')
    }
  }

  async generateSeasonChallenges(request: FastifyRequest, reply: FastifyReply) {
    try {
      const userId = request.user?.uid
      if (!userId) {
        return ApiResponse.unauthorized(reply)
      }

      const seasonId = this.challengeService.getCurrentSeasonId()
      const challenges = await this.challengeService.generateSeasonChallenges(userId, seasonId)
      return reply.send(challenges)
    } catch (error) {
      request.log.error(error)
      return ApiResponse.internalError(reply, 'Failed to generate season challenges')
    }
  }

  async updateChallengeProgress(
    request: FastifyRequest<{ 
      Params: { challengeId: string }
      Body: { increment: number }
    }>,
    reply: FastifyReply
  ) {
    try {
      const userId = request.user?.uid
      if (!userId) {
        return ApiResponse.unauthorized(reply)
      }

      const { challengeId } = request.params
      const { increment } = request.body

      const challenge = await this.challengeService.updateChallengeProgress(challengeId, increment)
      
      if (!challenge) {
        return ApiResponse.badRequest(reply, 'Challenge not found or not active')
      }

      return reply.send(challenge)
    } catch (error) {
      request.log.error(error)
      return ApiResponse.internalError(reply, 'Failed to update challenge progress')
    }
  }

  async generateAIChallengeIdea(request: FastifyRequest, reply: FastifyReply) {
    try {
      const userId = request.user?.uid
      if (!userId) {
        return ApiResponse.unauthorized(reply)
      }

      const idea = await this.challengeService.generateAIChallengeIdea(userId)
      return reply.send(idea)
    } catch (error) {
      request.log.error(error)
      return ApiResponse.internalError(reply, 'Failed to generate AI challenge idea')
    }
  }

  // ========== BINGO CARD ENDPOINTS ==========

  async createBingoCardForPlan(
    request: FastifyRequest<{ Params: { planId: string } }>,
    reply: FastifyReply
  ) {
    try {
      const userId = request.user?.uid
      if (!userId) {
        return ApiResponse.unauthorized(reply)
      }
      const { planId } = request.params
      const bingoCard = await this.bingoService.createBingoCard(planId, userId)
      return reply.status(201).send(bingoCard)
    } catch (error) {
      request.log.error(error, 'Failed to create bingo card')
      return ApiResponse.internalError(reply, 'Failed to create bingo card')
    }
  }

  async getBingoCardForPlan(
    request: FastifyRequest<{ Params: { planId: string } }>,
    reply: FastifyReply
  ) {
    try {
      const userId = request.user?.uid
      if (!userId) {
        return ApiResponse.unauthorized(reply)
      }
      const { planId } = request.params
      const bingoCard = await this.bingoService.getBingoCardByPlanId(planId)
      if (!bingoCard) {
        return ApiResponse.notFound(reply, 'Bingo card not found for this plan')
      }
      return reply.send(bingoCard)
    } catch (error) {
      request.log.error(error, 'Failed to get bingo card')
      return ApiResponse.internalError(reply, 'Failed to get bingo card')
    }
  }

  async completeBingoChallenge(
    request: FastifyRequest<{ Params: { challengeId: string } }>,
    reply: FastifyReply
  ) {
    try {
      const userId = request.user?.uid
      if (!userId) {
        return ApiResponse.unauthorized(reply)
      }
      const { challengeId } = request.params
      const updatedCard = await this.bingoService.completeChallenge(challengeId, userId)
      if (!updatedCard) {
        return ApiResponse.badRequest(reply, 'Could not complete challenge')
      }
      return reply.send(updatedCard)
    } catch (error) {
      request.log.error(error, 'Failed to complete bingo challenge')
      return ApiResponse.internalError(reply, 'Failed to complete bingo challenge')
    }
  }
}
